/**
 * Pick the card reader, and whether to keep the tapped card on file.
 *
 * Shared by the POS tender, the Record Payment modal and the enrollment
 * wizard, so the wording staff read — and the consent line for saving a
 * card — is the same at every desk.
 */
import React from "react";
import { TerminalReader } from "../../../api/terminalAPI";
import { useTheme } from "../../../context/ThemeContext";
import { getInputStyles, getSecondaryText } from "../../../utils/themeUtils";

interface ReaderPickerProps {
  readers: TerminalReader[];
  value: string | null;
  onChange: (readerId: string) => void;
  /** Whether "keep this card on file" can be offered (needs an account). */
  canSaveCard: boolean;
  saveCard: boolean;
  onSaveCardChange: (v: boolean) => void;
  disabled?: boolean;
}

const statusWord = (r: TerminalReader) => (r.status === "online" ? "online" : r.status || "unknown");

const ReaderPicker: React.FC<ReaderPickerProps> = ({
  readers,
  value,
  onChange,
  canSaveCard,
  saveCard,
  onSaveCardChange,
  disabled,
}) => {
  const { isDarkMode } = useTheme();

  if (readers.length === 0) {
    return (
      <p className="text-sm text-amber-600" data-testid="reader-picker-empty">
        No card reader is registered yet. Add one under Settings → Store, POS &amp; Shipping.
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-2" data-testid="reader-picker">
      <label htmlFor="reader-picker-select" className={`block text-sm ${getSecondaryText(isDarkMode)}`}>
        Card reader
      </label>
      <select
        id="reader-picker-select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full p-2 rounded-md ${getInputStyles(isDarkMode)}`}
      >
        {readers.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label || r.serial_number || r.id} ({statusWord(r)}){r.simulated ? " — simulated" : ""}
          </option>
        ))}
      </select>

      {canSaveCard && (
        <label className={`flex items-start gap-2 text-sm ${getSecondaryText(isDarkMode)}`}>
          <input
            type="checkbox"
            className="mt-0.5"
            checked={saveCard}
            onChange={(e) => onSaveCardChange(e.target.checked)}
            disabled={disabled}
            data-testid="reader-save-card"
          />
          <span>
            Keep this card on file for future payments. Ask the cardholder first — the reader
            does not ask.
          </span>
        </label>
      )}

      <p className={`text-xs ${getSecondaryText(isDarkMode)}`}>
        The amount is sent to the reader; the customer taps or inserts their card there.
      </p>
    </div>
  );
};

export default ReaderPicker;
